import { memo, useContext, useMemo, useState } from 'react'

import { AdUnitItem } from 'components/molecules/adBlock'
import { PromoteArticleMap } from 'components/pages/article/promoteArticles'
import innertext from 'innertext'
import { SpotlightSummary } from 'interfaces/types'
import { useRouter } from 'next/router'

import { ArticlePageContext } from '../../pages/article/index'
import { ArticleContentBlock } from './articleContentBlock'
import { sanitize } from './index'
import { StyledArticleContentWrapper } from './index.styled'
import { splitHtml2 } from './utils'

export type FormattedPostItem = {
  title?: string
  contents: FormattedPostItemContent[]
}

type InsertingContent = {
  titleData?: string
  contentData?: string
}
export type FormattedPostItemContent = {
  html: string
  injectedSlug?: string
  injectedHealthTool?: string
  adUnitItem?: Omit<AdUnitItem, 'adUnitPath'>
  injectedSpotlight?: Partial<SpotlightSummary>
  injectedCommunity?: {
    titleMember: string
    titleGuest: string
    descriptionMember: string
    descriptionGuest: string
    id: number | null
  }
  injectAffiliate?: {
    productId: number | null
    openNewTab: boolean
    noFollow: boolean
    sponsored: boolean
    title: string | null
  }

  isReadMore?: boolean
  isPremium?: boolean
  subotInline?: { subotId: number | string }
  injectFAQs?: boolean
  injectRecommendation?: InsertingContent
  injectCarousel?: {
    id: number
  }
  injectVideo?: {
    title: string
    description: string
    url: Array<string>
  }
  relatedArticles?: {
    ids: string
    title: string
  }
  is15Percent?: boolean
  is25Percent?: boolean
  is59Percent?: boolean
  is5Percent?: boolean
  is80Percent?: boolean
  is50Percent?: boolean
}

export type Props = {
  formattedPostContents: FormattedPostItem[]
  adUnitPath: string
  articleId: string
  spotlight?: SpotlightSummary
  postIntro?: string
  openAskDoctor?: (e: boolean) => void
  articleLink: string
  articleIsPremium?: boolean
  isLoggedIn?: boolean
  customTargetings?: { [key: string]: (string | number) | (string | number)[] }
}

// set minimum word count between ad
const MINIMUM_GAP = 800
// convert new line / image into word count to space out ad
const NEW_LINE_WORD_COUNT = 70
const IMAGE_WORD_COUNT = 600

const getWordCount = (htmlText: string) => {
  let count = 0
  count = innertext(htmlText).length + NEW_LINE_WORD_COUNT
  if (htmlText.includes('<img')) {
    count += (htmlText.split('<img').length - 1) * IMAGE_WORD_COUNT
  }
  if (htmlText.includes('<li')) {
    count += (htmlText.split('<li').length - 1) * NEW_LINE_WORD_COUNT
  }
  if (htmlText.includes('<amp-img')) {
    count += (htmlText.split('<amp-img').length - 1) * IMAGE_WORD_COUNT
  }
  return count
}

const READ_MORE_SLUGS: Record<string, string> = {}

const ArticleContentsComp = ({
  formattedPostContents,
  adUnitPath,
  articleId,
  spotlight,
  postIntro,
  openAskDoctor,
  articleLink,
  articleIsPremium,
  isLoggedIn,
  customTargetings,
}: Props) => {
  const articlePageContextValue = useContext(ArticlePageContext)
  const router = useRouter()
  const { locale = 'vi-VN' } = router
  const hasPromotionArticle =
    locale === 'vi-VN' && !!PromoteArticleMap[articleLink || '']
  const articleCleanSlug = articleLink.split('/').filter(Boolean).join('/')

  const { contentWithAd, endContents } = useMemo<{
    contentWithAd: FormattedPostItem[]
    endContents: FormattedPostItem
  }>(() => {
    let totalText = 0
    // Count all word in post
    formattedPostContents.forEach((section) => {
      const { contents } = section
      contents.forEach((content) => {
        const { html } = content
        if (html.includes('<p>')) {
          totalText += getWordCount(html)
        }
      })
    })
    // calculate gap between ads
    let maxAdCount = 2

    let gap = totalText / (maxAdCount + 1)
    if (gap < MINIMUM_GAP) {
      maxAdCount = Math.floor(totalText / MINIMUM_GAP) - 1
      if (maxAdCount <= 0) {
        maxAdCount = 1
      }
    }
    gap = totalText / (maxAdCount + 1)

    let adCount = 0
    let gapTextCount = 0
    // redo the steps to inject ad into article
    const renderContent = formattedPostContents.map((section) => {
      const { contents } = section
      let alreadyInsertReadMore = false
      const newContents = contents.reduce<FormattedPostItemContent[]>(
        (r, content) => {
          const { html, injectedSlug } = content
          if (html.includes('<p>')) {
            const res: FormattedPostItemContent[] = []

            let newContent: FormattedPostItemContent = { html: '' }
            const splittedHtml: string[] = splitHtml2(html)
            splittedHtml.forEach((text, index) => {
              // if previous iteration has no extension and only pure html, we keep it as is
              // else we cut it off and start new section
              if (Object.keys(newContent).length > 1 || !!newContent.html) {
                res.push(newContent)
                newContent = { html: '' }
              }
              if (
                !alreadyInsertReadMore &&
                hasPromotionArticle &&
                index === 1
              ) {
                alreadyInsertReadMore = true
                res.push(newContent)
                res.push({
                  html: '',
                  isReadMore: true,
                })
                newContent = { html: '' }
              }

              if (
                !alreadyInsertReadMore &&
                !hasPromotionArticle &&
                text.includes('class="continue-reading-articles')
              ) {
                alreadyInsertReadMore = true
                res.push(newContent)
                res.push({
                  html: '',
                  isReadMore: true,
                })
                newContent = { html: '' }
              }

              if (text.includes('class="flat-after-first-paragraph')) {
                res.push(newContent)
                res.push({
                  html: '',
                  isPremium: true,
                })
                newContent = { html: '' }
              }

              if (text.includes('[embed-health-tool-')) {
                newContent.injectedHealthTool =
                  /\[embed-health-tool-(.*?)]/gi.exec(text)?.[1] || ''
                return
              }

              if (text.includes('data-embed-type="health-tool')) {
                const healthToolTemplates = [
                  'bmi',
                  'bmr',
                  'ovulation',
                  'ovulation-calculator',
                  'due-date',
                  'due-date-calculator',
                  'child-growth-chart',
                  'preweight-gain-calculator',
                  'baby-poop-checking',
                  'vaccination-tool',
                ]
                healthToolTemplates.forEach((template) => {
                  if (text.includes(`data-template="${template}"`)) {
                    res.push(newContent)
                    res.push({
                      html: '',
                      injectedHealthTool: template,
                    })
                    newContent = { html: '' }
                  }
                })
                return
              }

              // Inject Related Articles
              if (text.includes('data-embed-type="related-articles"')) {
                const ids = /data-articles="(.*?)"/gi.exec(text)?.[1] || ''
                const title = /data-title="(.*?)"/gi.exec(text)?.[1] || ''
                newContent.relatedArticles = {
                  ids,
                  title,
                }
                return
              }

              if (sanitize(text)) {
                newContent.html += sanitize(text)
              }

              // 5% of the article
              if (text.includes('class="percentages-5-of-article')) {
                newContent.html = text
                newContent.is5Percent = true
                res.push(newContent)
                newContent = { html: '' }
                if (!alreadyInsertReadMore && hasPromotionArticle) {
                  alreadyInsertReadMore = true
                  res.push({
                    html: '',
                    isReadMore: true,
                  })
                }
                return
              }
              // 15% of the article
              if (text.includes('class="percentages-15-of-article')) {
                newContent.html = text
                newContent.is15Percent = true
                res.push(newContent)
                newContent = { html: '' }
                return
              }
              // 25% of the article
              if (text.includes('class="percentages-25-of-article')) {
                newContent.html = text
                newContent.is25Percent = true
                res.push(newContent)
                newContent = { html: '' }
                return
              }
              // 50% of the article
              if (text.includes('class="percentages-50-of-article')) {
                newContent.html = text
                newContent.is50Percent = true
                res.push(newContent)
                newContent = { html: '' }
                return
              }

              // 59% of the article
              if (text.includes('class="percentages-59-of-article')) {
                newContent.html = text
                newContent.is59Percent = true
                res.push(newContent)
                newContent = { html: '' }
                return
              }

              // 80% of the article
              if (text.includes('class="percentages-80-of-article')) {
                newContent.html = text
                newContent.is80Percent = true
                res.push(newContent)
                newContent = { html: '' }
                return
              }

              // Inject Community
              if (text.includes('data-embed-type="community"')) {
                const id = parseInt(/data-id="(.*?)"/gi.exec(text)?.[1] || '')
                if (!isNaN(id)) {
                  const titleMember =
                    /data-title-member="(.*?)"/gi.exec(text)?.[1] || ''
                  const titleGuest =
                    /data-title-guest="(.*?)"/gi.exec(text)?.[1] || ''
                  const descriptionMember =
                    /data-desc-member="(.*?)"/gi.exec(text)?.[1] || ''
                  const descriptionGuest =
                    /data-desc-guest="(.*?)"/gi.exec(text)?.[1] || ''

                  newContent.injectedCommunity = {
                    titleMember,
                    titleGuest,
                    descriptionMember,
                    descriptionGuest,
                    id: !isNaN(id) ? id : null,
                  }
                  return
                }
              }

              // Inject Spotlight
              if (text.includes('data-embed-type="spotlight"')) {
                const id = parseInt(/data-id="(.*?)"/gi.exec(text)?.[1] || '')

                newContent.injectedSpotlight = {
                  id: !isNaN(id) ? id : 0,
                }
                return
              }

              if (text.includes('[article-spotlight]')) {
                newContent.injectedSpotlight = spotlight
                return
              }

              // Inject Yoast FAQs
              if (text.includes('article-yoast-faqs')) {
                newContent.html = newContent.html.replace(
                  '[article-yoast-faqs]',
                  '',
                )
                newContent.injectFAQs = true
                return
              }

              // Inject Affiliate
              if (text.includes('data-embed-type="affiliate')) {
                const productId = /data-id="(.*?)"/gi.exec(text)?.[1] || ''
                if (productId) {
                  const openNewTab =
                    /data-new-tab="(.*?)"/gi.exec(text)?.[1] === 'true'
                  const noFollow =
                    /data-nofollow="(.*?)"/gi.exec(text)?.[1] === 'true'
                  const sponsored =
                    /data-sponsored="(.*?)"/gi.exec(text)?.[1] === 'true'

                  const title = /data-title="(.*?)"/gi.exec(text)?.[1] || ''

                  newContent.injectAffiliate = {
                    productId: Number(productId),
                    openNewTab,
                    noFollow,
                    sponsored,
                    title,
                  }
                  return
                }
              }

              // Inject Subot
              if (text.includes('data-embed-type="subot"')) {
                const subotId = parseInt(
                  /data-id="(.*?)"/gi.exec(text)?.[1] || '',
                )
                newContent.subotInline = {
                  subotId,
                }
                return
              }

              // Inject Carousel
              if (text.includes('data-embed-type="carousel"')) {
                const id = parseInt(/data-id="(.*?)"/gi.exec(text)?.[1] || '')

                newContent.injectCarousel = {
                  id,
                }
                return
              }

              if (text.includes('data-embed-type="video-embeb"')) {
                const title = /data-title="(.*?)"/gi.exec(text)?.[1] || ''
                const description =
                  /data-description="(.*?)"/gi.exec(text)?.[1] || ''
                const url = /data-url="(.*?)"/gi.exec(text)?.[1] || ''
                newContent.injectVideo = {
                  title,
                  description,
                  url: url.split(','),
                }
                return
              }

              gapTextCount += getWordCount(text)
              if (
                adCount === 0 ||
                (gapTextCount > gap && adCount <= maxAdCount)
              ) {
                if (adCount === 0) {
                  newContent.adUnitItem = {
                    divId: 'div-gpt-ad-topbanner-' + articleId,
                    size: [
                      [300, 250],
                      [300, 600],
                    ],
                    pos: ['TopBanner'],
                  }
                } else if (adCount === 1) {
                  newContent.adUnitItem = {
                    divId: 'div-gpt-ad-midbanner1-' + articleId,
                    size: [
                      [300, 250],
                      [300, 600],
                    ],
                    pos: ['MidBanner1'],
                  }
                } else if (adCount === 2) {
                  newContent.adUnitItem = {
                    divId: 'div-gpt-ad-midbanner2-' + articleId,
                    size: [
                      [300, 250],
                      [300, 600],
                    ],
                    pos: ['MidBanner2'],
                  }
                }
                adCount++
                gapTextCount = 0
              }
            })

            res.push(newContent)

            if (res[res.length - 1])
              res[res.length - 1].injectedSlug = injectedSlug
            return r.concat(res)
          }
          r.push(content)
          return r
        },
        [],
      )
      return {
        ...section,
        contents: newContents
          .map((c) => ({
            ...c,
            html:
              c.html
                ?.trim()
                .replace(/^(\s|&nbsp;)*<p><\/p>/g, '')
                .replace(/<p><\/p>(\s|&nbsp;)*$/g, '') || '',
          }))
          .filter((c) => !!c?.html || Object.keys(c).length > 1),
      }
    })

    const endContents: FormattedPostItem = {
      contents: [],
    }
    if (
      READ_MORE_SLUGS[locale]?.includes(articleCleanSlug) ||
      articleIsPremium
    ) {
      // walk backwards to seperate all contents without html props
      let breakOutOfLoop = false
      for (let i = 0; i < renderContent.length; i++) {
        const section = renderContent[renderContent.length - i - 1]
        if (section?.contents?.length) {
          for (let j = 0; j < section.contents.length; j++) {
            const subSection = {
              ...section.contents[section.contents.length - j - 1],
            }
            if (subSection.isReadMore || subSection.isPremium) {
              breakOutOfLoop = true
              break
            }
            const hasHtml =
              subSection.html &&
              (innertext(subSection.html) ||
                subSection.html.includes('<img loading="lazy" ') ||
                subSection.html.includes('<svg ') ||
                subSection.html.includes('<table ') ||
                subSection.html.includes('<li '))
            if (subSection.isReadMore || subSection.isPremium || hasHtml) {
              if (hasHtml) {
                subSection.html = ''
                endContents.contents.unshift(subSection)
                Object.keys(
                  section.contents[section.contents.length - j - 1],
                ).forEach((k) => {
                  if (k !== 'html')
                    delete section.contents[section.contents.length - j - 1][
                      k as keyof FormattedPostItemContent
                    ]
                })
              }
              breakOutOfLoop = true
              break
            }
            endContents.contents.unshift(subSection)
            delete section.contents[section.contents.length - j - 1]
          }
          if (breakOutOfLoop) {
            break
          }
        }
      }
    }

    return {
      contentWithAd: renderContent,
      endContents,
    }
  }, [formattedPostContents])

  const { normalContents, premiumContents, readMoreContents } = useMemo(() => {
    const normalContents: FormattedPostItem[] = []
    const premiumContents: FormattedPostItem[] = []
    const readMoreContents: FormattedPostItem[] = []

    let foundReadMore = false
    let foundPremium = false
    for (let i = 0; i < contentWithAd.length; i++) {
      if (articleIsPremium) {
        if (foundPremium) {
          premiumContents.push({ ...contentWithAd[i] })
          continue
        }
      } else if (foundReadMore) {
        readMoreContents.push({ ...contentWithAd[i] })
        continue
      }
      if (contentWithAd[i]?.contents?.length) {
        const tempNormalContents: FormattedPostItemContent[] = []
        const tempReadMoreContents: FormattedPostItemContent[] = []
        const tempPremiumContents: FormattedPostItemContent[] = []
        for (let j = 0; j < contentWithAd[i].contents.length; j++) {
          if (articleIsPremium && !isLoggedIn) {
            if (foundPremium) {
              tempPremiumContents.push(contentWithAd[i].contents[j])
              continue
            } else if (contentWithAd[i].contents[j]?.isPremium) {
              foundPremium = true
              tempPremiumContents.push(contentWithAd[i].contents[j])
              continue
            }
          } else {
            if (foundReadMore) {
              tempReadMoreContents.push(contentWithAd[i].contents[j])
              continue
            } else if (contentWithAd[i].contents[j]?.isReadMore) {
              foundReadMore = true
              tempReadMoreContents.push(contentWithAd[i].contents[j])
              continue
            }
          }
          tempNormalContents.push(contentWithAd[i].contents[j])
        }
        if (tempNormalContents.length) {
          normalContents.push({
            ...contentWithAd[i],
            contents: tempNormalContents,
          })
        }
        if (tempPremiumContents.length) {
          premiumContents.push({
            ...contentWithAd[i],
            contents: tempPremiumContents,
          })
        }
        if (tempReadMoreContents.length) {
          readMoreContents.push({
            ...contentWithAd[i],
            contents: tempReadMoreContents,
          })
        }
      }
    }

    return {
      normalContents,
      premiumContents,
      readMoreContents,
    }
  }, [contentWithAd, articleIsPremium, isLoggedIn])

  const [isShowAll, setIsShowAll] = useState(
    !READ_MORE_SLUGS[locale]?.includes(articleCleanSlug) &&
      !hasPromotionArticle,
  )
  const isShowAllPremium =
    !articleIsPremium || !premiumContents?.length || isLoggedIn

  return (
    <>
      {postIntro && (
        <StyledArticleContentWrapper
          className="article-content-wrapper"
          data-size={articlePageContextValue.articleFontSize || 'small'}
          style={{
            padding: 0,
            marginTop: '1rem',
          }}
          dangerouslySetInnerHTML={{
            __html: postIntro,
          }}
        />
      )}
      {normalContents.map((postContent, index) => {
        const { title, contents } = postContent
        return (
          <ArticleContentBlock
            articleLink={articleLink}
            key={index}
            contents={contents}
            isShowAllPremium
            isShowAll
            articleFontSize={articlePageContextValue.articleFontSize}
            articleId={articleId}
            openAskDoctor={openAskDoctor}
            adUnitPath={adUnitPath}
            title={title}
            customTargetings={customTargetings}
          />
        )
      })}
      {readMoreContents.map((postContent, index) => {
        const { title, contents } = postContent
        return (
          <ArticleContentBlock
            articleLink={articleLink}
            key={index}
            contents={contents}
            isShowAll={isShowAll}
            isShowAllPremium
            onReadMoreClick={() => setIsShowAll(true)}
            articleFontSize={articlePageContextValue.articleFontSize}
            articleId={articleId}
            openAskDoctor={openAskDoctor}
            adUnitPath={adUnitPath}
            title={title}
            isReadMoreBlock
            customTargetings={customTargetings}
          />
        )
      })}
      <div className={articleIsPremium ? 'paywall' : ''}>
        {premiumContents.map((postContent, index) => {
          const { title, contents } = postContent
          return (
            <ArticleContentBlock
              articleLink={articleLink}
              key={index}
              contents={contents}
              isShowAll
              isShowAllPremium={isShowAllPremium}
              articleFontSize={articlePageContextValue.articleFontSize}
              articleId={articleId}
              openAskDoctor={openAskDoctor}
              adUnitPath={adUnitPath}
              title={title}
              customTargetings={customTargetings}
            />
          )
        })}
      </div>
      {!!endContents?.contents?.length && (
        <ArticleContentBlock
          articleLink={articleLink}
          key={'end-contents'}
          contents={endContents.contents}
          isShowAll
          isShowAllPremium
          articleFontSize={articlePageContextValue.articleFontSize}
          articleId={articleId}
          openAskDoctor={openAskDoctor}
          adUnitPath={adUnitPath}
          customTargetings={customTargetings}
        />
      )}
    </>
  )
}

export const ArticleContents = memo(ArticleContentsComp)
